import { FC, useEffect, useMemo, useState } from "react";
import driverContractAddByRailyFormHelper from "./common/driver-contract-add-by-raily-form.helper";
import driverContractAddFactory from "../common/driver-contract-add.factory";
import driverContractAddByRailyFactory from "./common/factory/driver-contract-add-by-raily.factory";
import driverContractAddByRailyFormValidationService from "./driver-contract-add-by-raily-form-validation.service";
import DriverContractAddBillingModelSelectOption from "../common/types/driver-contract-add-billing-model-select-option";
import DriverContractAddViewBasicProps from "../common/types/driver-contract-add-view-basic-props";
import DriverContractAddByRailyTaxiAssociationSelectOption from "./common/types/driver-contract-add-by-raily-taxi-association-select-option";
import DriverContractAddByRailyFormData from "./common/types/driver-contract-add-by-raily-form-data";
import DriverContractAddByRailyFormValidationResults from "./common/types/driver-contract-add-by-raily-form-validation-results";
import ButtonComponent from "../../../../../../common/components/button/button.component";
import CardComponent from "../../../../../../common/components/card/card.component";
import DateRange from "../../../../../../common/types/date-range";
import FormFieldComponent from "../../../../../../common/components/form/field/form-field.component";
import NumericInputComponent from "../../../../../../common/components/form/input/numeric-input/numeric-input.component";
import Column from "../../../../../../common/components/grid/column";
import Row from "../../../../../../common/components/grid/row";
import HeadingComponent from "../../../../../../common/components/heading/heading.component";
import notificationService from "../../../../../../common/utils/notification/notification.service";
import userTranslationsHelper from "../../../../../../languages/user-translations.helper";
import { useNavigate } from "react-router-dom";
import userRoutesHelper from "../../../../common/routes/user-routes.helper";
import DateRangeInputComponent from "../../../../../../common/components/form/input/date-range/date-range-input.component";
import SingleSelectComponent from "../../../../../../common/components/form/select/single-select/single-select.component";
import BooleanSelectComponent from "../../../../../../common/components/form/select/boolean/boolean-select.component";
import InputComponent from "../../../../../../common/components/form/input/input.component";
import contractsService from "../../../../../../common/services/contracts/contracts.service";
import useAbort from "../../../../../../common/hooks/use-abort";
import useTaxiDriverAssociations from "../../../../../../common/services/driver/taxi-driver/associations/use-driver-taxi-driver-associations";
import { DriverTaxiDriverAssociationsData } from "../../../../../../common/services/driver/taxi-driver/associations/driver-taxi-driver-associations";
import DriverTaxiDriverAssociationsParams from "../../../../../../common/services/driver/taxi-driver/associations/driver-taxi-driver-associations-params";
import ContractsTaxiDriverAddParams from "../../../../../../common/services/contracts/taxi-driver/add/contracts-taxi-driver-add-params";

type DriverContractAddByRailyProps = DriverContractAddViewBasicProps;

const DriverContractAddByRailyComponent: FC<DriverContractAddByRailyProps> = (
  props,
) => {
  const navigate = useNavigate();
  const translations =
    userTranslationsHelper.getDriverContractAddTranslations();

  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const [formData, setFormData] = useState<DriverContractAddByRailyFormData>(
    () => driverContractAddByRailyFormHelper.getDefaultFormData(),
  );

  const [formValidationResults, setFormValidationResults] =
    useState<DriverContractAddByRailyFormValidationResults>(() =>
      driverContractAddByRailyFormHelper.getDefaultFormValidationResults(),
    );

  const contractTaxiDriverAddAbort = useAbort();

  const taxiDriverAssociations = useTaxiDriverAssociations();
  const taxiDriverAssociationsAbort = useAbort();

  const [taxiAssociationSelectOptions, setTaxiAssociationSelectOptions] =
    useState<DriverContractAddByRailyTaxiAssociationSelectOption[]>([]);

  const billingModelSelectOptions: DriverContractAddBillingModelSelectOption[] =
    useMemo(
      () => driverContractAddFactory.createBillingModelSelectOptions(),
      [],
    );

  const onTaxiAssociationsFetchSuccess = (
    responseTaxiAssociations: DriverTaxiDriverAssociationsData[],
  ) => {
    const taxiAssociationSelectOptions =
      driverContractAddByRailyFactory.createTaxiAssociationSelectOptions(
        responseTaxiAssociations,
      );

    setTaxiAssociationSelectOptions(taxiAssociationSelectOptions);
  };

  const fetchTaxiAssociations = () => {
    const params: DriverTaxiDriverAssociationsParams = {
      driverUuid: props.driverUuid!,
    };

    taxiDriverAssociations.load(params, taxiDriverAssociationsAbort.signal);
  };

  useEffect(() => {
    if (!taxiDriverAssociations.data) {
      return;
    }

    onTaxiAssociationsFetchSuccess(taxiDriverAssociations.data);
  }, [taxiDriverAssociations.data]);

  useEffect(() => {
    if (!props.driverUuid) {
      return;
    }

    fetchTaxiAssociations();
  }, [props.driverUuid]);

  const validateTaxiAssociation = (
    value: DriverContractAddByRailyFormData["taxiAssociation"] = formData.taxiAssociation,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateTaxiAssociation(
        value,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      taxiAssociation: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateBillingModel = (
    value: DriverContractAddByRailyFormData["billingModel"] = formData.billingModel,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateBillingModel(value);

    setFormValidationResults((curr) => ({
      ...curr,
      billingModel: validationResult,
    }));

    return validationResult.isValid;
  };

  const validatePeriodOfValidity = (
    value: DriverContractAddByRailyFormData["periodOfValidity"] = formData.periodOfValidity,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validatePeriodOfValidity(
        value,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      periodOfValidity: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateDistanceRate = (
    value: DriverContractAddByRailyFormData["distanceRate"] = formData.distanceRate,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateDistanceRate(value);

    setFormValidationResults((curr) => ({
      ...curr,
      distanceRate: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateHaltingTimeGracePeriodMinutes = (
    value: DriverContractAddByRailyFormData["haltingTimeGracePeriodMinutes"] = formData.haltingTimeGracePeriodMinutes,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateHaltingTimeGracePeriodMinutes(
        value,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      haltingTimeGracePeriod: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateHaltingTimeRate = (
    value: DriverContractAddByRailyFormData["haltingTimeRate"] = formData.haltingTimeRate,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateHaltingTimeRate(
        value,
      );

    setFormValidationResults((curr) => ({
      ...curr,
      haltingTimeRate: validationResult,
    }));

    return validationResult.isValid;
  };

  const checkIsFormValid = () => {
    const isTaxiAssociationValid = validateTaxiAssociation();
    const isModelValid = validateBillingModel();
    const isPeriodOfValidityValid = validatePeriodOfValidity();
    const isDistanceRateValid = validateDistanceRate();
    const isHaltingTimeGracePeriodMinutesValid =
      validateHaltingTimeGracePeriodMinutes();
    const isHaltingTimeRateValid = validateHaltingTimeRate();
    const isActiveValid = validateIsActive();

    return (
      isModelValid &&
      isTaxiAssociationValid &&
      isPeriodOfValidityValid &&
      isDistanceRateValid &&
      isHaltingTimeGracePeriodMinutesValid &&
      isHaltingTimeRateValid &&
      isActiveValid
    );
  };

  const onContractAddSuccess = () => {
    notificationService.success(translations.successAddNotificationText);

    navigate(
      userRoutesHelper.getDriverContractListingRoute({
        driverUuid: props.driverUuid!,
      }),
    );
  };

  const onContractAddFailure = () => {
    notificationService.error(translations.failureAddNotificationText);
  };

  const submitForm = () => {
    const isFormValid = checkIsFormValid();

    if (!isFormValid) return;

    setIsFormSubmitting(true);

    const params: ContractsTaxiDriverAddParams =
      driverContractAddFactory.createContractAddRequest(
        formData,
        formData.taxiAssociation?.value.uuid!,
      );

    contractsService
      .addTaxiDriverContract(params, contractTaxiDriverAddAbort.signal)
      .then(onContractAddSuccess)
      .catch(onContractAddFailure)
      .finally(() => setIsFormSubmitting(false));
  };

  const setTaxiAssociation = (
    value: DriverContractAddByRailyFormData["taxiAssociation"],
  ) => setFormData((curr) => ({ ...curr, taxiAssociation: value }));

  const setBillingModel = (
    value: DriverContractAddByRailyFormData["billingModel"],
  ) => setFormData((curr) => ({ ...curr, billingModel: value }));

  const setPeriodOfValidity = (
    value: DriverContractAddByRailyFormData["periodOfValidity"],
  ) => setFormData((curr) => ({ ...curr, periodOfValidity: value }));

  const setDistanceRate = (
    value: DriverContractAddByRailyFormData["distanceRate"] | undefined,
  ) => setFormData((curr) => ({ ...curr, distanceRate: value ?? null }));

  const setHaltingTimeGracePeriodMinutes = (
    value:
      | DriverContractAddByRailyFormData["haltingTimeGracePeriodMinutes"]
      | undefined,
  ) =>
    setFormData((curr) => ({
      ...curr,
      haltingTimeGracePeriodMinutes: value ?? null,
    }));

  const setHaltingTimeRate = (
    value: DriverContractAddByRailyFormData["haltingTimeRate"] | undefined,
  ) => setFormData((curr) => ({ ...curr, haltingTimeRate: value ?? null }));

  const setNotes = (
    value: DriverContractAddByRailyFormData["notes"] | undefined,
  ) => setFormData((curr) => ({ ...curr, notes: value ?? null }));

  const onTaxiAssociationChange = (
    value: DriverContractAddByRailyFormData["taxiAssociation"],
  ) => {
    setTaxiAssociation(value);
    validateTaxiAssociation(value);
  };

  const onBillingModelChange = (
    value: DriverContractAddByRailyFormData["billingModel"],
  ) => {
    setBillingModel(value);
    validateBillingModel(value);
  };

  const onPeriodOfValidityChange = (date: DateRange | null) => {
    setPeriodOfValidity(date);
    validatePeriodOfValidity(date);
  };

  const setIsActive = (value: DriverContractAddByRailyFormData["isActive"]) =>
    setFormData((curr) => ({
      ...curr,
      isActive: value,
    }));

  const validateIsActive = (
    value: DriverContractAddByRailyFormData["isActive"] = formData.isActive,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateIsActive(value);

    setFormValidationResults((curr) => ({
      ...curr,
      isActive: validationResult,
    }));

    return validationResult.isValid;
  };

  const validateNotes = (
    value: DriverContractAddByRailyFormData["notes"] = formData.notes,
  ) => {
    const validationResult =
      driverContractAddByRailyFormValidationService.validateNotes(value ?? "");

    setFormValidationResults((curr) => ({
      ...curr,
      notes: validationResult,
    }));

    return validationResult.isValid;
  };

  const isTaxiAssociationSelectEnabled = !taxiDriverAssociations.isError;

  return (
    <div className="driver_contract_add">
      <HeadingComponent
        title={
          props.driverName
            ? translations.header.headingTextWithName.replace(
                "#{driverName}",
                props.driverName,
              )
            : translations.header.headingText
        }
        actions={props.changeViewButtons}
      />
      <Row>
        <Column xl={8}>
          <CardComponent>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.taxiAssociationLabel}
                  classNames={{ root: `mt-0` }}
                  isRequired
                  errorMessage={
                    formValidationResults.taxiAssociation.errorMessage
                  }
                >
                  <SingleSelectComponent
                    isSearchable
                    placeholder={translations.form.taxiAssociationPlaceholder}
                    value={formData.taxiAssociation}
                    onChange={(value) => onTaxiAssociationChange(value)}
                    options={taxiAssociationSelectOptions}
                    isLoading={taxiDriverAssociations.isLoading}
                    isDisabled={!isTaxiAssociationSelectEnabled}
                    onBlur={validateTaxiAssociation}
                    hasError={
                      !!formValidationResults.taxiAssociation.errorMessage
                    }
                    idForTesting={`taxi-association`}
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.periodOfValidityLabel}
                  classNames={{ root: `mt-0` }}
                  isRequired
                  errorMessage={
                    formValidationResults.periodOfValidity.errorMessage
                  }
                >
                  <DateRangeInputComponent
                    onChange={onPeriodOfValidityChange}
                    date={formData.periodOfValidity}
                    hasError={
                      !!formValidationResults.periodOfValidity.errorMessage
                    }
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.billingModelLabel}
                  isRequired
                  errorMessage={formValidationResults.billingModel.errorMessage}
                >
                  <SingleSelectComponent
                    placeholder={translations.form.billingModelPlaceholder}
                    value={formData.billingModel}
                    onChange={(value) => onBillingModelChange(value)}
                    options={billingModelSelectOptions}
                    hasError={!!formValidationResults.billingModel.errorMessage}
                    idForTesting={`billing-model`}
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.distanceRateLabel}
                  isRequired
                  errorMessage={formValidationResults.distanceRate.errorMessage}
                >
                  <NumericInputComponent
                    placeholder={translations.form.distanceRatePlaceholder}
                    value={formData.distanceRate}
                    onChange={setDistanceRate}
                    onBlur={validateDistanceRate}
                    hasError={!!formValidationResults.distanceRate.errorMessage}
                    decimalPrecision={2}
                  />
                </FormFieldComponent>
              </Column>
            </Row>
            <Row>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.haltingTimeRateLabel}
                  isRequired
                  errorMessage={
                    formValidationResults.haltingTimeRate.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={formData.haltingTimeRate}
                    placeholder={translations.form.haltingTimeRatePlaceholder}
                    onChange={setHaltingTimeRate}
                    onBlur={validateHaltingTimeRate}
                    hasError={
                      !!formValidationResults.haltingTimeRate.errorMessage
                    }
                    decimalPrecision={2}
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.haltingTimeGracePeriodLabel}
                  isRequired
                  errorMessage={
                    formValidationResults.haltingTimeGracePeriod.errorMessage
                  }
                >
                  <NumericInputComponent
                    value={formData.haltingTimeGracePeriodMinutes}
                    placeholder={
                      translations.form.haltingTimeGracePeriodPlaceholder
                    }
                    onChange={setHaltingTimeGracePeriodMinutes}
                    onBlur={validateHaltingTimeGracePeriodMinutes}
                    isIntegerOnly
                    hasError={
                      !!formValidationResults.haltingTimeGracePeriod
                        .errorMessage
                    }
                  />
                </FormFieldComponent>
              </Column>
              <Column lg={6}>
                <FormFieldComponent
                  label={translations.form.isActiveLabel}
                  isRequired
                  errorMessage={formValidationResults.isActive.errorMessage}
                >
                  <BooleanSelectComponent
                    value={formData.isActive}
                    placeholder={translations.form.isActivePlaceholder}
                    onChange={setIsActive}
                    onBlur={validateIsActive}
                    isError={!!formValidationResults.isActive.errorMessage}
                  />
                </FormFieldComponent>
              </Column>
              <Column md={6}>
                <FormFieldComponent
                  label={translations.form.notesLabel}
                  errorMessage={formValidationResults.notes.errorMessage}
                >
                  <InputComponent
                    placeholder={translations.form.notesPlaceholder}
                    value={formData.notes!}
                    onChange={setNotes}
                    onBlur={validateNotes}
                    hasError={!!formValidationResults.notes.errorMessage}
                    idForTesting="notes-input"
                  />
                </FormFieldComponent>
              </Column>
            </Row>
          </CardComponent>
        </Column>
      </Row>
      <ButtonComponent
        onClick={submitForm}
        type="primary"
        isDisabled={isFormSubmitting}
        classNames={{ root: "mt-4" }}
        idForTesting={`submit-button`}
        title={translations.form.submitButtonTitle}
      >
        {translations.form.submitButtonText}
      </ButtonComponent>
    </div>
  );
};

export default DriverContractAddByRailyComponent;
